import { Immutable } from "immer";
import React from "react";
import { hasRichTextContent } from "tsi-common-react/src/utils/streamfieldContent";

import type {
    PageChooserBlock,
    StearnsCompetitionCompareTableBlock as Value,
} from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type IProps = Immutable<{
    value: Value;
}>;

type Feature = NonNullable<Value["features"]>[number];
type Icon = Value["our_affirmative_icon"];

// The legacy template rendered every status icon via `{% image ... width-50 %}`,
// i.e. a 50px-wide `<img>`. This block's `ImageChooserBlock`s use the default
// `extra_client_data_renditions` (`{"thumbnail": "fill-100x100"}`), so the
// serialized `renditions` map has NO `width-50` key -- only `original`,
// `original--webp` and `thumbnail`. Use the original (top-level `url`, which
// preserves aspect ratio -- `thumbnail` crops via `fill-`) and pin the display
// size to 50x50 so the row height matches the legacy baseline. Adding a
// `width-50` rendition would be a Python `extra_client_data_renditions` change;
// reproducing the legacy display size in React keeps the change in-scope.
const ICON_SIZE = 50;

const StatusIcon = (
    props: Immutable<{ icon: Icon; alt: string }>,
): React.JSX.Element | null => {
    if (!props.icon) {
        return null;
    }
    return (
        <img
            src={props.icon.url}
            alt={props.alt}
            width={ICON_SIZE}
            height={ICON_SIZE}
            loading="lazy"
            decoding="async"
        />
    );
};

// Renders the legacy `<a class="button" href="{{ dest.url }}">{{ text }}</a>`.
// Gated on `destination` so callers needn't narrow the nullable page chooser.
const CTAButton = (
    props: Immutable<{
        destination: PageChooserBlock | null | undefined;
        text: string | null | undefined;
    }>,
): React.JSX.Element | null => {
    if (!props.destination) {
        return null;
    }
    return (
        <a className="button" href={props.destination.url}>
            {props.text}
        </a>
    );
};

// The "our" and competitor columns share identical yes/no/text rendering; the
// only difference is which affirmative icon they pass and that the "our" column
// can append an inline CTA (on the last feature row, when there is NO separate
// CTA row, the legacy markup put a CTA button inside the yes/text cell). Mirror
// the Sealy variant's unified `OurOrCompetitorCell`: one cell with an optional
// `cta` node (omitted -> the competitor column; the `no` branch never renders a
// CTA in either column, matching the legacy markup).
const FeatureCell = (
    props: Immutable<{
        // `our_feature_status` and `competition_feature_status` are the same
        // status union; one annotation covers both call sites.
        status: Feature["our_feature_status"];
        text: string | null | undefined;
        affirmativeIcon: Icon;
        negativeIcon: Icon;
        cta?: React.ReactNode;
    }>,
): React.JSX.Element | null => {
    if (props.status === "yes") {
        return (
            <td>
                {!props.text ? (
                    <StatusIcon icon={props.affirmativeIcon} alt="We have it" />
                ) : (
                    <div>
                        <StatusIcon
                            icon={props.affirmativeIcon}
                            alt="We have it"
                        />
                        {props.text}
                    </div>
                )}
                {props.cta}
            </td>
        );
    }
    if (props.status === "no") {
        return (
            <td>
                {!props.text ? (
                    <StatusIcon
                        icon={props.negativeIcon}
                        alt="We don't have it"
                    />
                ) : (
                    <div>
                        <StatusIcon
                            icon={props.negativeIcon}
                            alt="We don't have it"
                        />
                        {props.text}
                    </div>
                )}
            </td>
        );
    }
    if (props.status === "text") {
        return (
            <td>
                {props.text}
                {props.cta}
            </td>
        );
    }
    return null;
};

// The legacy second-competitor column rendered ONLY a bare icon for yes/no (no
// text wrapper / no `feature_text`), and `second_competition_feature_text` for
// the `text` status.
const SecondCompetitionCell = (
    props: Immutable<{
        status: Feature["second_competition_feature_status"];
        text: string | null | undefined;
        affirmativeIcon: Icon;
        negativeIcon: Icon;
    }>,
): React.JSX.Element | null => {
    if (props.status === "yes") {
        return (
            <td>
                <StatusIcon icon={props.affirmativeIcon} alt="We have it" />
            </td>
        );
    }
    if (props.status === "no") {
        return (
            <td>
                <StatusIcon icon={props.negativeIcon} alt="We don't have it" />
            </td>
        );
    }
    if (props.status === "text") {
        return <td>{props.text}</td>;
    }
    return null;
};

// `hasRichTextContent` (shared `tsi-common-react/src/utils/streamfieldContent`) gates the
// optional 4th (second-competitor) column on REAL content: an unset RichText
// field serializes to an empty-but-TRUTHY `<div class="rich-text"></div>` (the
// shared base uses a CharBlock, where unset is an empty string and thus falsy),
// so a `!!value` check would add a phantom empty column that widened the table
// and overflowed at narrow (Print/Tablet) viewports.

/**
 * Stearns-local port of the shared `competition-compare-table-block.html`.
 *
 * Four deltas from the shared (cocoon) variant, all driven by stearns'
 * divergent serialized shape:
 *
 * 1. `logo` is a RichText HTML string (the legacy `logo_is_richtext` branch):
 *    render raw via `dangerouslySetInnerHTML`, matching the legacy
 *    `{% include_block value.logo %}`. `competitor_name`,
 *    `second_competitor_name`, `fineprint` and each `feature.feature_name` are
 *    RichText too. All serialize via
 *    `ClientRenderingBarrier.get_data_for_client_render -> render() ->`
 *    (no `Meta.template`) `-> render_basic -> force_str`, i.e. the BARE expanded
 *    RichText HTML (`wagtailcore/shared/richtext.html` is just `{{ html|safe }}`,
 *    no `.rich-text` wrapper) -- the exact same output the legacy
 *    `{% include_block %}` and `{{ feature.feature_name }}` produced (both also
 *    route through `render_basic`). Rendering raw reproduces the legacy DOM
 *    byte-for-byte; `<RichTextBlock>` (thelabui) would add a wrapper `<div>` the
 *    legacy DOM lacked.
 * 2. Separate CTA row (the legacy `use_separate_cta_row` branch). The legacy
 *    `get_context` flag was true when stearns set a competitor CTA; it is now
 *    derived client-side as `cta_destination || competitor_cta_destination`
 *    (the row renders both our-CTA and competitor-CTA buttons, each gated on its
 *    own destination). When the separate row shows, the feature rows do NOT get
 *    the inline trailing CTA (so the last feature row has no `no-cta` class).
 * 3. Both highlight classes on the `<table>`:
 *    `highlight--{our_column_background_color}` and
 *    `highlight__competitor-column--{competitor_column_background_color}`.
 * 4. `feature.feature_name` is RichText (vs Char on the shared base).
 */
export const StearnsCompetitionCompareTableBlock = (
    props: IProps,
): React.JSX.Element => {
    const v = props.value;
    const features: readonly Feature[] = v.features ?? [];

    // Legacy `use_separate_cta_row`: true when either CTA destination is set.
    const useSeparateCTARow =
        !!v.cta_destination || !!v.competitor_cta_destination;

    // The 4th column exists only when a second competitor is actually named
    // (see `hasRichTextContent`); the `<tbody>` already gates its 4th cell on
    // `second_competition_feature_status`, so this keeps `<thead>`/`<tbody>`
    // column counts in agreement.
    const hasSecondCompetitor = hasRichTextContent(v.second_competitor_name);

    const sectionClassName = concatClassNames([
        "competition-compare-table-block",
        v.background_style?.style,
        v.responsive_visibility?.visibility,
        v.theme,
        hasSecondCompetitor
            ? "competition-compare-table-block--four-columns"
            : null,
        // Section class `--with-cta` gates on `cta_destination` ONLY (matches
        // the legacy markup, distinct from the separate-CTA-row condition).
        v.cta_destination ? "competition-compare-table-block--with-cta" : null,
    ]);

    const tableClassName = concatClassNames([
        // The table's light cells and highlights set their own text colors,
        // so it opts out of the dark-background white text
        // (_background-styles.scss).
        "keeps-text-color",
        v.our_column_background_color
            ? `highlight--${v.our_column_background_color}`
            : null,
        v.competitor_column_background_color
            ? `highlight__competitor-column--${v.competitor_column_background_color}`
            : null,
    ]);

    return (
        <section className={sectionClassName}>
            <table className={tableClassName}>
                <thead>
                    <tr>
                        <th scope="col">
                            <p className="ada-screenreader-only">Features</p>
                        </th>
                        <th
                            scope="col"
                            dangerouslySetInnerHTML={{ __html: v.logo ?? "" }}
                        />
                        <th
                            scope="col"
                            dangerouslySetInnerHTML={{
                                __html: v.competitor_name ?? "",
                            }}
                        />
                        {hasSecondCompetitor ? (
                            <th
                                scope="col"
                                dangerouslySetInnerHTML={{
                                    __html: v.second_competitor_name ?? "",
                                }}
                            />
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {features.map((feature, i) => {
                        const isLast = i === features.length - 1;
                        // Inline trailing CTA on the last row's "our" cell only
                        // when NOT using the separate CTA row (legacy behavior).
                        const inlineCTA = isLast && !useSeparateCTARow;
                        // The legacy `<tr>` got `last` on the final row, plus
                        // `no-cta` only when the inline (non-separate) CTA path
                        // was in play. With a separate CTA row there is no
                        // inline CTA, so `no-cta` is omitted.
                        const rowClassName = concatClassNames([
                            isLast ? "last" : null,
                            inlineCTA ? "no-cta" : null,
                        ]);
                        return (
                            <tr key={i} className={rowClassName}>
                                <th
                                    scope="row"
                                    dangerouslySetInnerHTML={{
                                        __html: feature.feature_name ?? "",
                                    }}
                                />
                                <FeatureCell
                                    status={feature.our_feature_status}
                                    text={feature.our_feature_text}
                                    affirmativeIcon={v.our_affirmative_icon}
                                    negativeIcon={v.negative_icon}
                                    cta={
                                        <CTAButton
                                            destination={
                                                inlineCTA
                                                    ? v.cta_destination
                                                    : null
                                            }
                                            text={v.cta_text}
                                        />
                                    }
                                />
                                <FeatureCell
                                    status={feature.competition_feature_status}
                                    text={feature.competition_feature_text}
                                    affirmativeIcon={v.affirmative_icon}
                                    negativeIcon={v.negative_icon}
                                />
                                {hasSecondCompetitor ? (
                                    <SecondCompetitionCell
                                        status={
                                            feature.second_competition_feature_status
                                        }
                                        text={
                                            feature.second_competition_feature_text
                                        }
                                        affirmativeIcon={v.affirmative_icon}
                                        negativeIcon={v.negative_icon}
                                    />
                                ) : null}
                            </tr>
                        );
                    })}
                    {useSeparateCTARow ? (
                        <tr>
                            <th />
                            {v.cta_destination ? (
                                <td>
                                    <CTAButton
                                        destination={v.cta_destination}
                                        text={v.cta_text}
                                    />
                                </td>
                            ) : null}
                            {v.competitor_cta_destination ? (
                                <td>
                                    <CTAButton
                                        destination={
                                            v.competitor_cta_destination
                                        }
                                        text={v.competitor_cta_text}
                                    />
                                </td>
                            ) : null}
                        </tr>
                    ) : null}
                </tbody>
            </table>
            {v.fineprint ? (
                <div
                    className="fineprint"
                    dangerouslySetInnerHTML={{ __html: v.fineprint }}
                />
            ) : null}
        </section>
    );
};
